import type { BrowserClientMessage, BrowserElementCapture, BrowserServerMessage, BrowserState } from "@codvero/shared";
import { useCallback, useEffect, useReducer, useRef } from "react";
import { wsUrl } from "@/api/chats.ts";

export interface TabMeta { url: string; title: string; loading: boolean; canGoBack: boolean; canGoForward: boolean }
export interface ConsoleEntry { tabId: string; level: string; text: string; at: number }
export interface NetworkEntry { tabId: string; id: string; method: string; url: string; status: number | null; resourceType: string | null }

interface S {
  connected: boolean;
  state: BrowserState | null;
  tab: TabMeta | null;
  viewTabId: string | null;
  console: ConsoleEntry[];
  network: NetworkEntry[];
  error: string | null;
  lastCapture: BrowserElementCapture | null;
  downloads: { fileName: string; state: string }[];
}
type A = { type: "conn"; v: boolean } | { type: "msg"; m: BrowserServerMessage } | { type: "viewTab"; id: string | null } | { type: "clearCapture" } | { type: "clearLogs" };

function reduce(s: S, a: A): S {
  switch (a.type) {
    case "conn":
      return { ...s, connected: a.v, error: a.v ? null : s.error };
    case "viewTab":
      return { ...s, viewTabId: a.id, tab: null };
    case "clearCapture":
      return { ...s, lastCapture: null };
    case "clearLogs":
      return { ...s, console: [], network: [] };
    case "msg": {
      const m = a.m;
      switch (m.type) {
        case "state":
          return { ...s, state: m.state, viewTabId: s.viewTabId && m.state.tabs.some((t) => t.id === s.viewTabId) ? s.viewTabId : m.state.activeTabId };
        case "tab":
          // "tab" is always about the tab this viewer is attached to (the server picks it on attach/create/select).
          return { ...s, viewTabId: m.tabId, tab: { url: m.url, title: m.title, loading: m.loading, canGoBack: m.canGoBack, canGoForward: m.canGoForward } };
        case "console":
          return { ...s, console: [...s.console.slice(-299), { tabId: m.tabId, level: m.level, text: m.text, at: m.at }] };
        case "network": {
          const idx = s.network.findIndex((n) => n.id === m.id);
          if (idx >= 0) {
            const next = s.network.slice();
            next[idx] = { ...next[idx]!, status: m.status ?? next[idx]!.status, resourceType: m.resourceType ?? next[idx]!.resourceType };
            return { ...s, network: next };
          }
          return { ...s, network: [...s.network.slice(-499), { tabId: m.tabId, id: m.id, method: m.method, url: m.url, status: m.status, resourceType: m.resourceType }] };
        }
        case "picker_selected":
          return { ...s, lastCapture: m.capture };
        case "download":
          return { ...s, downloads: [...s.downloads.slice(-9), { fileName: m.fileName, state: m.state }] };
        case "error":
          return { ...s, error: m.message ?? m.code };
        default:
          return s;
      }
    }
  }
}

/**
 * Owns the /ws/browser/:projectId socket. JPEG frames arrive as binary (0x01 + 36-char tabId + jpeg)
 * and are handed to `onFrame` directly so React never re-renders per frame.
 */
export function useBrowserSession(projectId: string, enabled: boolean, onFrame: (tabId: string, blob: Blob) => void) {
  const [s, dispatch] = useReducer(reduce, { connected: false, state: null, tab: null, viewTabId: null, console: [], network: [], error: null, lastCapture: null, downloads: [] });
  const wsRef = useRef<WebSocket | null>(null);
  const sizeRef = useRef({ width: 1280, height: 800, scale: 1 });
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;

  const send = useCallback((m: BrowserClientMessage) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(m));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let closed = false;
    let retry = 1000;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const connect = () => {
      const ws = new WebSocket(wsUrl(`/ws/browser/${projectId}`));
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;
      ws.onopen = () => {
        retry = 1000;
        dispatch({ type: "conn", v: true });
        const { width, height, scale } = sizeRef.current;
        ws.send(JSON.stringify({ type: "attach", tabId: null, width, height, scale } satisfies BrowserClientMessage));
      };
      ws.onmessage = (e) => {
        if (typeof e.data === "string") dispatch({ type: "msg", m: JSON.parse(e.data) as BrowserServerMessage });
        else {
          const buf = new Uint8Array(e.data as ArrayBuffer);
          if (buf[0] !== 0x01) return;
          const tabId = new TextDecoder().decode(buf.subarray(1, 37)).trim();
          onFrameRef.current(tabId, new Blob([buf.subarray(37)], { type: "image/jpeg" }));
        }
      };
      ws.onclose = (e) => {
        dispatch({ type: "conn", v: false });
        if (closed || e.code === 4401 || e.code === 4403 || e.code === 4400) return;
        timer = setTimeout(connect, retry);
        retry = Math.min(retry * 2, 8000);
      };
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(timer);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [projectId, enabled]);

  const resize = useCallback((width: number, height: number, scale: number) => {
    sizeRef.current = { width, height, scale };
    send({ type: "resize", width, height, scale });
  }, [send]);
  const selectTab = useCallback((id: string) => {
    dispatch({ type: "viewTab", id });
    send({ type: "tabs.select", tabId: id });
  }, [send]);

  return { ...s, send, resize, selectTab, clearCapture: () => dispatch({ type: "clearCapture" }), clearLogs: () => dispatch({ type: "clearLogs" }) };
}
